import {GUDRID_SESSION_EVENTS} from 'constants/gudrid-session-event-types';
import {
    EVENT_LOG,
    EVENTS_DATA,
    LAERDAL_EVENT_LOG,
    LAERDAL_VITAL_SIGNS_LOG,
    VITAL_SIGNS_LOG,
} from 'constants/LU_ApplicationDataTypes';
import * as Immutable from 'immutable';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import union from 'lodash/union';
import {createReducer} from 'redux-store';
import * as RecordingUtils from 'utils/recording-utils';
import {returnEmptyIfNull} from 'utils/string-utils';
import * as UserUtils from 'utils/user-utils';
import * as constants from '../constants/session-viewer-constants';

import type {VideoSource} from 'constants/video-sources';
import type {List, Map, MapOf} from 'immutable';
import type {ActionsMap} from 'redux-store';

export type RecordedFileJS = Partial<{
    bucket: string;
    bucketKey: string;
    bucketRegion: string;
    captureNodeId: string | null;
    captureNodeSessionId: string | null;
    channelId: string | null;
    cloudDeleteRequestTs: string | null;
    cloudDeleteTs: string | null;
    durationInSec: number;
    hasAudio: boolean;
    hlsBucket: string | null;
    hlsPlaylistKey: string | null;
    hlsStatus: string | null;
    localDeleteRequestConfirmedTs: string | null;
    localDeleteRequestTs: string | null;
    localDeleteTs: string | null;
    recordedFileExternalId: string;
    recordedFileId: string;
    remoteHls: string | null;
    remoteHttp: string | null;
    sizeInBytes: number;
    sort: number;
    spritesBucket: string | null;
    spritesFrameDuration: number;
    spritesFrameHeight: number;
    spritesFrameWidth: number;
    spritesFramesPerSprite: number;
    spritesKeyPrefix: string | null;
    spritesQuality: number;
    spritesStatus: string | null;
    startRequestConfirmedTs: string | null;
    startRequestTs: string | null;
    startTs: string | null;
    stopRequestConfirmedTs: string | null;
    stopRequestTs: string | null;
    stopTs: string | null;
    uploadStartTs: string | null;
    uploadStatus: string | null;
    uploadStopTs: string | null;
}>;

export type RecordedFile = MapOf<RecordedFileJS>;

export type VcRecording = Partial<{
    deleteRequestTs: string | null;
    recordedFiles: List<RecordedFile>;
    simulatorSessionFiles: List<unknown>;
    startRequestConfirmedTs: string | null;
    startRequestTs: string | null;
    startTs: string | null;
    stopRequestConfirmedTs: string | null;
    stopRequestTs: string | null;
    stopTs: string | null;
    videoConductorSessionId: string | null;
}>;

export type RecordingJS = Partial<{
    clientId: string;
    examPhaseId: string | null;
    recordingId: string | null;
    sessionId: string | null;
    vcRecording: MapOf<VcRecording>;
    vcRecordingId: string | null;
}>;

export type Recording = MapOf<RecordingJS>;

export type Session = Partial<{
    inventoryManagementItems: List<unknown>;
    startTs: Date;
    notes: string;
    recordings: List<Recording> | null;
    offsetMilliseconds: number;
    preventDeletion: boolean;
    reservationId: string;
    acl: List<string>;
    sessionTypeId: string;
    scenarioId: string;
    recordingTranscripts: List<unknown>;
    live: boolean;
    reservationTitle: string;
    organizationId: string;
    simulators: List<unknown>;
    sessionType: string;
    scenario: Map<unknown, unknown>;
    locationId: string;
    sessionId: string;
    facilitatorUserId: unknown;
    organization: MapOf<{
        active: boolean;
        name: string;
    }>;
    location: unknown;
    reservationScenarioId: string;
    title: string;
    links: List<unknown>;
    endTs: string;
    examId: unknown;
    reservationInventoryItems: List<unknown>;
    videoSource: VideoSource;
}>;

export type SessionViewerStore = {
    session: MapOf<Session>;
    getSessionFailed: boolean;

    simGraphDisplay: boolean;
    displaySidebar: boolean;
    displayEventLogFilters: boolean;
    displayEventLogSimulations: boolean;
    displayEventLogAnnotations: boolean;
    displayEventLogGudrid: boolean;
    onSidebarToggled: () => unknown;
    // search results for editing each type of user
    users: Map<string, List<unknown>>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    people: Map<string, any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    permissionGroups: Map<string, any>;
    sharedWith: List<unknown>;

    activity: List<unknown>;
    locations: List<unknown>;
    markAsViewedTime: number;
    organizations: List<unknown>;
    scenarios: List<unknown>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    simulators: List<Map<string, any>>;
    viewCount: number;

    recordingAnnotations: List<unknown>;
    recordingAnnotationsLoaded: boolean;
    simulatorEventsLoaded: boolean;
    simulatorEventsPriorities: List<unknown>;
    selectedRecordingIndex: number;
    recordingFiles: List<Map<unknown, unknown>>;
    simulatorSessionFiles: List<unknown>;
    recordingFilesOriginalOrder: List<Map<unknown, unknown>>;
    scenarioPredefinedAnnotations: List<unknown>;
    simulatorEvents: List<Map<unknown, unknown>>;
    simulatorStats: List<unknown>;
    simulatorStatsExist: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gudridSessionLatestVitals: Immutable.Map<string, any> | null;
    gudridSessionEventsAll: List<unknown>;
    gudridSessionEventsLoaded: boolean;
    exportedEventLogFileName: string;
    questions: List<unknown>;
    applicationFiles: List<unknown>;
    pausedIntervals: List<unknown>;
    simulatorOffset: number;
    isTrimming: boolean;
    updatedEvaluationIds: string[];
    sidebarMetadata?: unknown;
};

const getApplicationEvents = (applicationFiles) => {
    let eventLog = applicationFiles.find(
        (a) => a.getIn(['applicationDataType', 'ref']) === EVENTS_DATA,
        this,
        Immutable.Map(),
    );

    if (eventLog.isEmpty()) {
        eventLog = applicationFiles.find(
            (a) =>
                a.getIn(['applicationDataType', 'ref']) === LAERDAL_EVENT_LOG ||
                a.getIn(['applicationDataType', 'ref']) === EVENT_LOG,
            this,
            Immutable.Map(),
        );
    }
    return eventLog.getIn(['data', 'events'], Immutable.List()).map((d) => {
        if (isNil(d.get('offsetMilliSeconds'))) {
            d = d.set('offsetSeconds', parseInt(d.get('offsetSeconds')));
        } else {
            // @ts-expect-error TS(2345) FIXME: Argument of type 'number' is not assignable to par... Remove this comment to see the full error message
            d = d.set('offsetSeconds', parseInt(d.get('offsetMilliSeconds') / 1000));
        }
        d = d.set('priority', parseInt(d.get('priority')));
        return d;
    });
};

const getApplicationStats = (applicationFiles) => {
    return applicationFiles
        .find(
            (a) =>
                a.getIn(['applicationDataType', 'ref']) === LAERDAL_VITAL_SIGNS_LOG ||
                a.getIn(['applicationDataType', 'ref']) === VITAL_SIGNS_LOG,
            this,
            Immutable.Map(),
        )
        .getIn(['data', 'vitals'], Immutable.List())
        .map((s) => {
            s = s.set(
                'offsets',
                s.get('data').map((d) => d.get('offsetSeconds')),
            );
            s = s.set(
                'values',
                s.get('data').map((d) => d.get('value')),
            );
            return s;
        });
};
const getApplicationStatStart = (applicationFiles) => {
    return applicationFiles
        .find(
            (a) =>
                a.getIn(['applicationDataType', 'ref']) === LAERDAL_VITAL_SIGNS_LOG ||
                a.getIn(['applicationDataType', 'ref']) === VITAL_SIGNS_LOG,
            this,
            Immutable.Map(),
        )
        .getIn(['data', 'startTs']);
};

const filterAsEvents = (e, simulatorEvents) => {
    return isNil(
        simulatorEvents.find((se) => {
            return se.get('text') === e.getIn(['annotation', 'text']);
        }),
    );
};

const extractPausedIntervals = (recordingAnnotations: List<Map<string, number>>) => {
    let pauseResumeItems = Immutable.List<Map<string, number>>();
    let pausedIntervals = Immutable.List<Map<string, number>>();
    recordingAnnotations.forEach((r) => {
        if (r.getIn(['annotation', 'text']) === '-- Pause') {
            pauseResumeItems = pauseResumeItems.push(
                // @ts-expect-error TS(2345) FIXME: Argument of type 'number' is not assignable to parameter of type 'unknown'.
                Immutable.Map<string, number>({
                    offsetSeconds: r.get('offsetSeconds'),
                    pause: true,
                }),
            );
        }
        if (r.getIn(['annotation', 'text']) === '-- Resume') {
            pauseResumeItems = pauseResumeItems.push(
                // @ts-expect-error TS(2345) FIXME: Argument of type 'number' is not assignable to parameter of type 'unknown'.
                Immutable.Map<string, number>({
                    offsetSeconds: r.get('offsetSeconds'),
                    pause: false,
                }),
            );
        }
    });

    pauseResumeItems = pauseResumeItems.sort((p) => p.get('offsetSeconds'));
    pauseResumeItems.forEach((p, i) => {
        if (p.get('pause')) {
            const next = pauseResumeItems.get(i + 1, Immutable.Map<string, number>());
            if (!next.isEmpty() && !next.get('pause')) {
                pausedIntervals = pausedIntervals.push(
                    Immutable.Map<string, number>({
                        pauseOffsetSeconds: p.get('offsetSeconds'),
                        resumeOffsetSeconds: next.get('offsetSeconds'),
                        offsetSeconds: next.get('offsetSeconds') - p.get('offsetSeconds'),
                    }),
                );
            }
        }
    });
    return pausedIntervals;
};

// helper to safely parse and normalize gudrid parameters
const parseGudridParameters = (parametersRaw) => {
    if (!parametersRaw) {
        return {};
    }

    let parsedParameters: Record<string, unknown>;

    if (typeof parametersRaw === 'string') {
        try {
            const parsed = JSON.parse(parametersRaw);
            parsedParameters = parsed && typeof parsed === 'object' ? parsed : {};
        } catch (err) {
            console.error('failed to parse gudrid parameters', err, parametersRaw);
            return {};
        }
    } else if (typeof parametersRaw.toJS === 'function') {
        try {
            parsedParameters = parametersRaw.toJS() || {};
        } catch {
            return {};
        }
    } else if (typeof parametersRaw === 'object') {
        parsedParameters = parametersRaw;
    } else {
        return {};
    }

    // the heart rate vital can come in as either 'heartRate' or 'bpm' depending on the event, so normalize to 'bpm' for consistency
    if ('heartRate' in parsedParameters) {
        parsedParameters['bpm'] = parsedParameters.heartRate;
        delete parsedParameters['heartRate'];
    }

    return parsedParameters;
};

// helper to merge new parameters into the latest vitals state
const mergeGudridLatestVitals = (currentLatest, newParametersRaw) => {
    const newParameters = parseGudridParameters(newParametersRaw);
    const latestVitals = currentLatest || Immutable.Map();
    return latestVitals.mergeDeep(Immutable.fromJS(newParameters));
};

export const getInitialState = (): SessionViewerStore => {
    return {
        session: Immutable.Map({}),
        getSessionFailed: false,

        simGraphDisplay: false,
        displaySidebar: false,
        displayEventLogFilters: false,
        displayEventLogSimulations: true,
        displayEventLogAnnotations: true,
        displayEventLogGudrid: true,
        onSidebarToggled: noop,
        // search results for editing each type of user
        users: Immutable.fromJS({
            owner: [],
            contributors: [],
            participants: [],
            patient: [],
            viewers: [],
        }),
        people: Immutable.Map(),
        permissionGroups: Immutable.Map({
            participants: Immutable.List(),
            patient: Immutable.List(),
            viewers: Immutable.List(),
            contributors: Immutable.List(),
        }),
        sharedWith: Immutable.List(),

        activity: Immutable.List(),
        locations: Immutable.List(),
        markAsViewedTime: constants.SESSION_VIEW_TIMER,
        organizations: Immutable.List(),
        scenarios: Immutable.List(),
        simulators: Immutable.List(),
        viewCount: 0,

        recordingAnnotations: Immutable.List(),
        recordingAnnotationsLoaded: false,
        simulatorEventsLoaded: false,
        simulatorEventsPriorities: Immutable.List(),
        selectedRecordingIndex: 0,
        recordingFiles: Immutable.List(),
        simulatorSessionFiles: Immutable.List(),
        recordingFilesOriginalOrder: Immutable.List<Map<unknown, unknown>>(),
        scenarioPredefinedAnnotations: Immutable.List(),
        simulatorEvents: Immutable.List(),
        simulatorStats: Immutable.List(),
        simulatorStatsExist: false,
        gudridSessionLatestVitals: Immutable.Map(),
        gudridSessionEventsAll: Immutable.List(),
        gudridSessionEventsLoaded: false,
        exportedEventLogFileName: undefined,
        questions: Immutable.List(),
        applicationFiles: Immutable.List(),
        pausedIntervals: Immutable.List(),
        simulatorOffset: 0,
        isTrimming: false,
        updatedEvaluationIds: [],
    };
};

export const actionsMap: ActionsMap<SessionViewerStore> = {
    [constants.SESSION_SUCCESS]: (state, action) => {
        let session = returnEmptyIfNull(action.data, ['notes']);
        let {simulatorEventsLoaded, scenarioPredefinedAnnotations} = state;

        const recordingsWithFilesReady = RecordingUtils.getRecordingsWithFilesReady(session);
        const selectedRecordingIndex = !isNil(recordingsWithFilesReady.get(state.selectedRecordingIndex))
            ? state.selectedRecordingIndex
            : 0;

        const recordingAtIndex = recordingsWithFilesReady.get(selectedRecordingIndex);
        const selectedRecording = !isNil(recordingAtIndex)
            ? Immutable.List<Recording>().push(recordingAtIndex)
            : Immutable.List<Recording>();

        const recordingFiles = RecordingUtils.getRecordedFilesFromRecordings(selectedRecording);
        const nonDeletedRecordingFiles = recordingFiles.filter((f) => isNil(f.get('cloudDeleteRequestTs')));

        const simulatorSessionFiles = RecordingUtils.getSimulatorSessionFileId(session);
        if (simulatorSessionFiles.isEmpty()) {
            simulatorEventsLoaded = true;
        }

        if (session.hasIn(['scenario', 'scenarioAnnotations'])) {
            scenarioPredefinedAnnotations = session.getIn(['scenario', 'scenarioAnnotations']);
            session = session.deleteIn(['scenario', 'scenarioAnnotations']);
        }

        return {
            session,
            scenarioPredefinedAnnotations,
            recordingFiles: nonDeletedRecordingFiles,
            selectedRecordingIndex,
            simulatorSessionFiles,
            simulatorEventsLoaded,
            recordingFilesOriginalOrder: nonDeletedRecordingFiles.map((f) => f), // Required to create a new instance so when modifying the sort the original order is maintained
        };
    },

    [constants.SESSION_FAILURE]: () => {
        return {
            getSessionFailed: true,
        };
    },

    [constants.SESSION_ANNOTATIONS_SUCCESS]: (state, action) => {
        return {
            recordingAnnotations: action.data.filter((ra) => filterAsEvents(ra, state.simulatorEvents)),
            pausedIntervals: extractPausedIntervals(action.data),
            recordingAnnotationsLoaded: true,
        };
    },
    [constants.DISPOSE_SESSION_DETAIL_STORE]: () => {
        return getInitialState();
    },
    [constants.REORDER_STREAMS]: (state, action) => {
        const max = Math.max(action.sourceIndex, action.targetIndex);
        const min = Math.min(action.sourceIndex, action.targetIndex);
        const swapItemMax = state.recordingFiles.get(max);
        const swapItemMin = state.recordingFiles.get(min);
        let newRecordingList = state.recordingFiles.remove(max);
        newRecordingList = newRecordingList.insert(min, swapItemMax);
        newRecordingList = newRecordingList.remove(min + 1);
        newRecordingList = newRecordingList.insert(max, swapItemMin);

        return {
            recordingFiles: newRecordingList,
        };
    },
    [constants.UPDATE_SELECTED_RECORDING]: (state, action) => {
        const {index} = action;
        const recordingsWithFilesReady = RecordingUtils.getRecordingsWithFilesReady(state.session);
        const recordingFiles = RecordingUtils.getRecordedFilesFromRecordings(
            Immutable.List<Recording>().push(recordingsWithFilesReady.get(index)),
        );
        const nonDeletedRecordingFiles = recordingFiles.filter((f) => isNil(f.get('cloudDeleteRequestTs')));
        return {
            selectedRecordingIndex: index,
            recordingFiles: nonDeletedRecordingFiles,
            recordingFilesOriginalOrder: nonDeletedRecordingFiles.map((f) => f), // Required to create a new instance so when modifying the sort the original order is maintained
        };
    },
    [constants.TOGGLE_SIM_GRAPH]: (state, action) => {
        return {
            simGraphDisplay: action.simGraphDisplay,
        };
    },
    [constants.TOGGLE_SLIDING_PANEL]: (state, action) => {
        return {
            displaySidebar: action.displaySidebar,
        };
    },

    [constants.SET_ON_SIDEBAR_TOGGLED]: (state, action) => {
        return {
            onSidebarToggled: action.onSidebarToggled,
        };
    },
    [constants.TOGGLE_EVENT_LOG_FILTERS]: (state, action) => {
        return {
            displayEventLogFilters: action.displayEventLogFilters,
        };
    },

    [constants.SESSION_PEOPLE_SUCCESS]: (state, action) => {
        return {
            people: action.data,
        };
    },
    [constants.SESSION_PEOPLE_DEFAULTS_SUCCESS]: (state, action) => {
        return {
            permissionGroups: action.data,
        };
    },
    [constants.SCENARIOS_SUCCESS]: (state, action) => {
        return {
            scenarios: action.data,
        };
    },

    [constants.ORGANIZATIONS_SUCCESS]: (state, action) => {
        return {
            organizations: action.data,
        };
    },

    [constants.LOCATIONS_SUCCESS]: (state, action) => {
        return {
            locations: action.data,
        };
    },

    [constants.SIMULATORS_SUCCESS]: (state, action) => {
        return {
            simulators: action.data.sortBy((simulator) => {
                return simulator.get('name');
            }),
        };
    },

    [constants.SEARCH_OWNER_SUCCESS]: (state, action) => {
        const users = action.data;
        const sortedUsers = users.sortBy((user) => {
            return UserUtils.getLastNameFirst(user);
        });

        return {
            users: state.users.set('owner', sortedUsers),
        };
    },
    [constants.SEARCH_CONTRIBUTORS_SUCCESS]: (state, action) => {
        const users = action.data;
        const sortedUsers = users.sortBy((user) => {
            return UserUtils.getLastNameFirst(user);
        });

        return {
            users: state.users.set('contributors', sortedUsers),
        };
    },
    [constants.SEARCH_PARTICIPANTS_SUCCESS]: (state, action) => {
        const users = action.data;
        const sortedUsers = users.sortBy((user) => {
            return UserUtils.getLastNameFirst(user);
        });

        return {
            users: state.users.set('participants', sortedUsers),
        };
    },
    [constants.SEARCH_PATIENT_SUCCESS]: (state, action) => {
        const users = action.data;
        const sortedUsers = users.sortBy((user) => {
            return UserUtils.getLastNameFirst(user);
        });

        return {
            users: state.users.set('patient', sortedUsers),
        };
    },

    [constants.SEARCH_VIEWERS_SUCCESS]: (state, action) => {
        const users = action.data;
        const sortedUsers = users.sortBy((user) => {
            return UserUtils.getLastNameFirst(user);
        });

        return {
            users: state.users.set('viewers', sortedUsers),
        };
    },

    [constants.SESSION_SIMULATOR_EVENTS_SUCCESS]: (state, action) => {
        return {
            simulatorEvents: action.data
                .get('events', Immutable.List())
                .map((event) => {
                    return event.set('offsetSeconds', event.get('pat_time'));
                })
                .concat(getApplicationEvents(state.applicationFiles)),
            simulatorEventsPriorities: action.data.get('priorities'),
            simulatorEventsLoaded: true,
        };
    },

    [constants.SESSION_APPLICATION_FILES_SUCCESS]: (state, action) => {
        const simulatorEvents = state.simulatorEvents.concat(getApplicationEvents(action.data));
        const simulatorEventsPriorities = [];
        simulatorEvents.forEach((e) => {
            simulatorEventsPriorities.push(e.get('priority'));
        });

        let simulatorOffset = 0;
        const startTs = state.session.get('startTs');
        const simulatorStatsStartTs = getApplicationStatStart(action.data);
        if (!isNil(startTs) && !isNil(simulatorStatsStartTs)) {
            simulatorOffset = (simulatorStatsStartTs.getTime() - startTs.getTime()) / 1000;
        }

        return {
            applicationFiles: action.data,
            simulatorStats: getApplicationStats(action.data).concat(state.simulatorStats),
            simulatorOffset,
            recordingAnnotations: state.recordingAnnotations.filter((ra) =>
                filterAsEvents(ra, getApplicationEvents(action.data)),
            ),
            simulatorEvents,
            simulatorEventsPriorities: Immutable.List(union(simulatorEventsPriorities)),
            simulatorStatsExist: state.simulatorStatsExist ? true : !action.data.isEmpty(),
        };
    },

    [constants.SESSION_SIMULATOR_STATS_SUCCESS]: (state, action) => {
        return {
            simulatorStats: action.data,
            simulatorStatsExist: true,
        };
    },

    [constants.TOGGLE_EVENT_LOG_SIMULATIONS]: (state, action) => {
        return {
            displayEventLogSimulations: action.displayEventLogSimulations,
        };
    },

    [constants.TOGGLE_EVENT_LOG_ANNOTATIONS]: (state, action) => {
        return {
            displayEventLogAnnotations: action.displayEventLogAnnotations,
        };
    },

    [constants.TOGGLE_EVENT_LOG_GUDRID]: (state, action) => {
        return {
            displayEventLogGudrid: action.displayEventLogGudrid,
        };
    },

    // for live gudrid session data
    [constants.GUDRID_SESSION_DATA_RECEIVED]: (state, action) => {
        const newEvent = action.gudridData;
        const parameters = newEvent.get('parameters');
        const isBundleCommandEvent = newEvent.get('eventType') === GUDRID_SESSION_EVENTS.BUNDLE_COMMANDS;

        const vitalsData = isBundleCommandEvent ? JSON.parse(parameters).vitals : parameters;

        return {
            gudridSessionEventsAll: state.gudridSessionEventsAll.push(newEvent),
            gudridSessionLatestVitals: mergeGudridLatestVitals(state.gudridSessionLatestVitals, vitalsData),
        };
    },

    [constants.SET_GUDRID_EVENTS_LOADED]: () => {
        return {
            gudridSessionEventsLoaded: true,
        };
    },

    [constants.SESSION_GUDRID_EVENTS_REQUEST]: () => {
        return {
            gudridSessionEventsLoaded: false, // Set loading to false when request starts
        };
    },

    // for loading historical gudrid events
    [constants.SESSION_GUDRID_EVENTS_SUCCESS]: (state, action) => {
        const historicalEvents = Immutable.fromJS(action.data || []).map((event) => {
            return Immutable.Map({
                type: 'gudrid',
                eventType: event.get('eventType'),
                text: `${event.get('eventType')}: ${JSON.stringify(event.get('parameters'))}`,
                parameters: JSON.stringify(event.get('parameters')),
                timestamp: event.get('timestamp'),
                offsetSeconds: 0,
                gudridSessionId: event.get('gudridSessionId'),
            });
        });

        // rebuild gudridSessionLatestVitals from all historical events
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let latestVitals = Immutable.Map<string, any>();
        historicalEvents.forEach((event) => {
            const isBundleCommandEvent = event.get('eventType') === GUDRID_SESSION_EVENTS.BUNDLE_COMMANDS;
            const vitalsParametersRaw = isBundleCommandEvent
                ? JSON.parse(event.get('parameters')).vitals
                : event.get('parameters');
            latestVitals = mergeGudridLatestVitals(latestVitals, vitalsParametersRaw);
        });

        return {
            gudridSessionEventsAll: state.gudridSessionEventsAll.concat(historicalEvents),
            gudridSessionLatestVitals: latestVitals,
            gudridSessionEventsLoaded: true,
        };
    },

    [constants.SESSION_SWITCH]: () => {
        return getInitialState();
    },

    [constants.SESSION_HISTORY_SUCCESS]: (state, action) => {
        return {
            activity: action.data,
        };
    },

    [constants.SESSION_VIEW_COUNT_SUCCESS]: (state, action) => {
        return {
            viewCount: action.data,
        };
    },

    [constants.DELETE_SIMULATOR_DATA_SUCCESS]: () => {
        return {
            simulatorStats: Immutable.List(),
            simulatorEvents: Immutable.List(),
            simGraphDisplay: false,
            simulatorSessionFiles: Immutable.List(),
        };
    },

    [constants.EXPORT_EVENT_LOG_SUCCESS]: (state, action) => {
        return {
            exportedEventLogFileName: action.data,
        };
    },
    [constants.SET_SESSION]: (state, action) => {
        return {
            session: action.session,
        };
    },
    [constants.CLEAR_SESSION_TRANSCRIPTS]: (state) => {
        const {session} = state;
        return {
            session: session.set('recordingTranscripts', Immutable.List()),
        };
    },
    [constants.SET_SESSION_TRANSCRIPTS]: (state, action) => {
        const {session} = state;
        return {
            session: session.set('recordingTranscripts', action.transcripts),
        };
    },
    [constants.SET_TRIMMING_STATE]: (state, action) => {
        return {
            isTrimming: action.isTrimming,
        };
    },
    [constants.ADD_UPDATED_EVALUATIONS]: (state, action) => {
        const newIds = [...new Set([...state.updatedEvaluationIds, ...action.evaluationIds])];

        return {
            ...state,
            updatedEvaluationIds: newIds,
        };
    },
    [constants.CLEAR_EVALUATION_UPDATE]: (state, action) => {
        const filtered = state.updatedEvaluationIds.filter((id) => id !== action.evaluationId);

        return {
            ...state,
            updatedEvaluationIds: filtered,
        };
    },
};

export default createReducer<SessionViewerStore>(getInitialState(), actionsMap);
